import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { mcpServerUrlSchema } from "../../../../shared/domain/mcp/mcp-server-url.schema";

/**
 * The authentication half of a registration.
 *
 * A discriminated union rather than one flat object with optional fields,
 * because the database already refuses the combinations a flat object would
 * admit: `McpServer_auth_header_name_iff_header_mode` is a biconditional, so a
 * header name without HEADER mode and HEADER mode without a header name are both
 * 23514s. A flat schema would turn an operator's mistake into a 500 whose message
 * names a constraint; this turns it into a 400 that names the field.
 *
 * OAUTH carries NOTHING but its discriminant, and every omission is deliberate:
 *
 * - no `authHeaderName` — `McpServer_auth_header_name_iff_header_mode` refuses one
 *   outside HEADER mode;
 * - no client id or secret — the OAuth client is established per ISSUER when the
 *   flow starts (`EnsureMcpOAuthClientUseCase`), and one authorization server can
 *   guard several servers, so a client typed in beside one server would be a
 *   second, competing client for every other server that issuer guards.
 *
 * Registering an OAUTH server writes no credential. The server reports
 * `AUTHORIZATION_REQUIRED` on sync until `POST /api/mcp-servers/:id/oauth/start`
 * has been completed, which is exactly what that code means.
 */
const RegisterMcpServerAuthSchema = z.discriminatedUnion("authMode", [
  z.object({ authMode: z.literal("NONE") }),
  z.object({ authMode: z.literal("OAUTH") }),
  z.object({
    authMode: z.literal("HEADER"),
    /**
     * An HTTP field name, held to the same token grammar as
     * `McpServer_auth_header_name_is_token`. Refused here so the failure is a 400
     * naming the field rather than a 23514 naming a constraint.
     */
    authHeaderName: z
      .string()
      .regex(
        /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/,
        "authHeaderName must be a valid HTTP field name (RFC 9110 token)"
      ),
    /**
     * WRITE-ONLY. Encrypted into `ToolCredentials.apikey` and returned by no
     * endpoint on this surface, ever.
     */
    authHeaderSecret: z.string().min(1, "authHeaderSecret must not be blank")
  })
]);

export const RegisterMcpServerBodySchema = z
  .object({
    /**
     * The operator's label for the server. It becomes the `AgentExternalTool`'s
     * `privateName`, which is the identifier a model sees, so it cannot be blank.
     */
    name: z.string().trim().min(1, "name must not be blank").max(200),
    /**
     * HTTPS, not a private or internal address, no userinfo, canonicalised on the
     * way through. The canonical form is what `@@unique([organizationId, url])`
     * compares, so two spellings of one endpoint are one server.
     */
    url: mcpServerUrlSchema,
    /**
     * Streamable HTTP is the only transport. Stated rather than defaulted away: a
     * second transport is an additive enum value, and a client that never sent
     * the field would silently keep getting the first one.
     */
    transport: z.enum(DbEnum.MCP_TRANSPORT_VALUES).default("STREAMABLE_HTTP")
  })
  .and(RegisterMcpServerAuthSchema);

export type RegisterMcpServerBody = z.infer<typeof RegisterMcpServerBodySchema>;
